import type { WeatherState } from '@/core/types';

/** Normalised effect drive levels derived from the weather state (unit-tested, pure). */
export interface WeatherIntensity {
  /** 0..1 rain streak density. */
  rain: number;
  /** Multiplier on drop fall speed. */
  dropSpeed: number;
  /** -1..1 horizontal wind drift (positive = blowing right). */
  wind: number;
  /** 0..1 leaf particle rate. */
  leaves: number;
  /** 0..1 ground splash rate (0 indoors). */
  splash: number;
  /** 0..1 rain ripple rate on water. */
  ripples: number;
  /** 0..1 heat-haze strength. */
  haze: number;
  /** Expected lightning flashes per second. */
  lightning: number;
  /** 0..1 suggested scene darkening (informational; the world/lighting builder applies tint). */
  darken: number;
}

const BASE: Record<WeatherState, WeatherIntensity> = {
  sultry: { rain: 0, dropSpeed: 1, wind: 0.12, leaves: 0.08, splash: 0, ripples: 0, haze: 1, lightning: 0, darken: 0 },
  wind: { rain: 0, dropSpeed: 1, wind: 0.9, leaves: 1, splash: 0, ripples: 0.1, haze: 0.25, lightning: 0, darken: 0.08 },
  drizzle: { rain: 0.35, dropSpeed: 0.8, wind: 0.35, leaves: 0.4, splash: 0.45, ripples: 0.55, haze: 0, lightning: 0.015, darken: 0.2 },
  rain: { rain: 1, dropSpeed: 1, wind: 0.5, leaves: 0.25, splash: 1, ripples: 1, haze: 0, lightning: 0.08, darken: 0.35 },
  'interior-rain': { rain: 0.7, dropSpeed: 1, wind: 0.2, leaves: 0, splash: 0.3, ripples: 0.8, haze: 0, lightning: 0.05, darken: 0.3 },
};

export const WEATHER_STATES: readonly WeatherState[] = ['sultry', 'wind', 'drizzle', 'rain', 'interior-rain'];

export function baseIntensity(state: WeatherState): WeatherIntensity {
  return { ...(BASE[state] ?? BASE.sultry) };
}

export function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : Number.isNaN(v) ? 0 : v; }

/** Linear blend of two intensity sets at t in [0,1] (t clamped). */
export function blendIntensity(from: WeatherIntensity, to: WeatherIntensity, t: number): WeatherIntensity {
  const k = clamp01(t);
  const out = {} as WeatherIntensity;
  for (const key of Object.keys(to) as (keyof WeatherIntensity)[]) out[key] = from[key] + (to[key] - from[key]) * k;
  return out;
}

/** Intensity when transitioning from `from` to `to`, `t` = progress (0 = still `from`, 1 = fully `to`). */
export function intensityFor(to: WeatherState, t = 1, from: WeatherState = to): WeatherIntensity {
  return blendIntensity(baseIntensity(from), baseIntensity(to), t);
}

/** Reduce-motion scaling: fewer, slower particles; no haze; lightning tamed. */
export function applyReduceMotion(i: WeatherIntensity, on: boolean): WeatherIntensity {
  if (!on) return i;
  return { ...i, rain: i.rain * 0.45, dropSpeed: i.dropSpeed * 0.85, leaves: i.leaves * 0.4, splash: i.splash * 0.5, ripples: i.ripples * 0.6, haze: 0, lightning: i.lightning * 0.5 };
}

/** Streaks to spawn this fixed step (fractional; caller accumulates). Capacity-aware. */
export function rainSpawnPerStep(rain: number, viewWidth: number, maxDrops: number): number {
  const perStep = rain * (viewWidth / 640) * 14;
  return Math.min(perStep, maxDrops / 20);
}
